import React from 'react';
import { PackageMinus } from 'lucide-react';
import { ProductOptionalComponent } from '../types';

interface OptionalComponentSelectorProps {
  components: ProductOptionalComponent[];
  removedComponents: string[];
  onChange: (removedComponents: string[]) => void;
  className?: string;
}

const OptionalComponentSelector: React.FC<OptionalComponentSelectorProps> = ({
  components,
  removedComponents,
  onChange,
  className = '',
}) => {
  if (!components.length) return null;

  const sorted = [...components].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );

  const toggleComponent = (componentId: string, included: boolean) => {
    if (included) {
      onChange(removedComponents.filter((id) => id !== componentId));
    } else if (!removedComponents.includes(componentId)) {
      onChange([...removedComponents, componentId]);
    }
  };

  return (
    <div className={`mb-6 ${className}`}>
      <h3 className="text-lg font-bold text-white mb-2 flex items-center">
        <PackageMinus className="mr-2" size={20} />
        Anpassa paketet
      </h3>
      <p className="text-sm text-gray-400 mb-4">
        Allt ingår som standard. Avmarkera det du inte behöver för att få rabatt.
      </p>
      <div className="space-y-3">
        {sorted.map((component) => {
          const isIncluded = !removedComponents.includes(component.id);
          const discountLabel =
            component.pricing_type === 'per_day'
              ? `−${component.discount} kr/dag`
              : `−${component.discount} kr`;

          return (
            <label
              key={component.id}
              className={`flex items-start gap-3 p-4 border rounded-none cursor-pointer transition-colors ${
                isIncluded
                  ? 'border-gray-600 bg-gray-900/50 hover:border-purple-500/50'
                  : 'border-purple-500/40 bg-purple-500/10'
              }`}
            >
              <input
                type="checkbox"
                checked={isIncluded}
                onChange={(e) => toggleComponent(component.id, e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-500 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-white">{component.name}</span>
                  {!isIncluded && (
                    <span className="text-sm font-medium text-emerald-400">{discountLabel}</span>
                  )}
                </div>
                {component.description && (
                  <p className="text-sm text-gray-400 mt-1">{component.description}</p>
                )}
                {isIncluded && (
                  <p className="text-xs text-gray-500 mt-1">Ingår i paketet</p>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export function isIncludeItemRemoved(
  includeText: string,
  components: ProductOptionalComponent[],
  removedComponents: string[]
): boolean {
  const removedSet = new Set(removedComponents);
  return components.some((component) => {
    if (!removedSet.has(component.id)) return false;
    if (!component.includes_match) return false;
    return includeText.toLowerCase().includes(component.includes_match.toLowerCase());
  });
}

export function getRemovedComponentLabels(
  components: ProductOptionalComponent[],
  removedComponents: string[] | undefined
): string[] {
  if (!removedComponents?.length) return [];
  const removedSet = new Set(removedComponents);
  return components
    .filter((component) => removedSet.has(component.id))
    .map((component) => component.name);
}

export default OptionalComponentSelector;
